import {
  Alert, Box, Chip, Divider, Stack, Tooltip, Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslate } from 'react-admin';
import { Asset, SecurityInventoryCategory, CertificationCategory } from '@x-guard/xgac-types/xgac';
import { calculateDaysUntilDate } from '../../../utils/certificationsAndItemsExpiry';
import { convertTimeGranularityToDays } from '../../../utils/timeGranularityUtils';

type NextEmailAlertProps = {
  type: 'security-inventory';
  category: SecurityInventoryCategory | null;
  users: Asset[];
  certifiedUserName?: never;
} | {
  type: 'certification';
  category: CertificationCategory | null;
  users: Asset[];
  certifiedUserName: string | null;
};

export const NextEmailAlert = (props: NextEmailAlertProps) => {

  const translate = useTranslate();
  const expiryDate = useWatch({ name: 'expiryDate' }) as string | undefined;
  const plannedRenewalDate = useWatch({ name: 'plannedRenewalDate' }) as string | undefined;

  const { responsibleUsers, certifiedUser } = useMemo(() => {

    const peopleToNotifyIds = new Set(
      (props.category?.notifiedPeople.peopleToNotify ?? [])
        .map((person) => (typeof person === 'string' ? person : person._id)),
    );

    const users = props.users.filter((user) => peopleToNotifyIds.has(user._id)).map((user) => ({ id: user._id, label: user.name }));
    const emails = (props.category?.notifiedPeople.emails ?? []).map((email) => ({ id: `email:${email}`, label: email }));

    if (props.type === 'certification' && props.category?.notifiedPeople.isCertificationHolderNotified && props.certifiedUserName) {

      return { responsibleUsers: [...users, ...emails], certifiedUser: props.certifiedUserName };

    }

    return { responsibleUsers: [...users, ...emails] };

  }, [props.category, props.users, props.type, props.certifiedUserName]);

  const daysUntilNextEmail = useMemo(() => {

    const daysUntilPlannedRenewalDate = plannedRenewalDate ? calculateDaysUntilDate(plannedRenewalDate) : null;
    if (daysUntilPlannedRenewalDate !== null && daysUntilPlannedRenewalDate >= 0) {

      return daysUntilPlannedRenewalDate;

    }

    if (!expiryDate || !props.category) {

      return null;

    }

    const daysUntilExpiry = calculateDaysUntilDate(expiryDate);
    const reminderPeriods = [
      ...props.category.reminderPeriods.map((reminderPeriod) => (
        daysUntilExpiry - convertTimeGranularityToDays(reminderPeriod)
      )),
      daysUntilExpiry,
    ].filter((daysUntilEmail) => daysUntilEmail >= 0);

    return reminderPeriods.length > 0 ? Math.min(...reminderPeriods) : daysUntilExpiry;

  }, [plannedRenewalDate, expiryDate, props.category]);

  if (!props.category || daysUntilNextEmail === null) {

    return null;

  }

  const numberOfRecipients = certifiedUser ? responsibleUsers.length + 1 : responsibleUsers.length;
  const recipientsLabel = numberOfRecipients === 1
    ? certifiedUser || responsibleUsers[0].label
    : translate('general.text.users', { count: numberOfRecipients });

  const isExpired = daysUntilNextEmail < 0;
  const hasRecipients = numberOfRecipients > 0;

  return (
    <Alert
      severity={!hasRecipients || isExpired ? 'warning' : 'info'}
      color={!hasRecipients || isExpired ? 'warning' : 'success'}
      variant="outlined"
      sx={{ alignItems: 'center' }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5,
      }}>
        {isExpired && (
          <Typography>
            {props.type === 'security-inventory'
              ? translate('general.text.will_not_be_sent_item_expired', { smart_count: Math.abs(daysUntilNextEmail) })
              : translate('general.text.will_not_be_sent_certification_expired', { smart_count: Math.abs(daysUntilNextEmail) })}
          </Typography>
        )}
        {!isExpired && !hasRecipients && (
          <Typography>{translate('general.text.email_will_not_be_sent', { smart_count: numberOfRecipients })}</Typography>
        )}
        {!isExpired && hasRecipients && (
          <Typography component="div" sx={{ width: '100%' }}>
            {numberOfRecipients === 1
              ? (<Chip label={recipientsLabel} size="small" />)
              : (
                <Tooltip
                  title={
                    <Stack spacing={1}>
                      {responsibleUsers.length > 0 && (
                        <Box>
                          <Typography>{translate('general.text.responsible_users', { smart_count: responsibleUsers.length })}</Typography>
                          {responsibleUsers.map((recipient) => (
                            <Typography key={recipient.id}>{recipient.label}</Typography>
                          ))}
                        </Box>
                      )}
                      {responsibleUsers.length > 0 && certifiedUser && (
                        <Divider sx={{ height: '2px', backgroundColor: 'white' }} />
                      )}
                      {certifiedUser && (
                        <Box>
                          <Typography>
                            {translate('general.text.certified_users')}
                          </Typography>
                          <Typography>{certifiedUser}</Typography>
                        </Box>
                      )}
                    </Stack>
                  }
                >
                  <Chip label={recipientsLabel} size="small" />
                </Tooltip>
              )
            }
            {' '}
            {translate('general.text.will_receive', { smart_count: numberOfRecipients, categoryName: props.category.name })}
            {props.type === 'security-inventory' ? (<br />) : ' '}
            {daysUntilNextEmail === 0
              ? translate('general.text.will_be_sent_today')
              : translate('general.text.email_will_be_sent', { smart_count: daysUntilNextEmail })}
          </Typography>
        )}
      </Box>
    </Alert>
  );

};
